import {

  AfterContentInit,
  ContentChildren,
  Directive,
  Host,
  OnDestroy,
  OnInit,
  QueryList,
  Renderer2,
} from '@angular/core';
import { DropdownComponent } from '@appkit4/angular-components/dropdown';
import { DropdownListItemComponent } from '@appkit4/angular-components/dropdown-list-item';
import { Subscription } from 'rxjs';

/**
 * When ap-dropdown uses projected ap-dropdown-list-item children (enableNgContent),
 * AppKit filters internally but does not hide non-matching projected rows.
 * This directive syncs visibility + highlight text with onFilterResult.
 */
@Directive({
  selector: 'ap-dropdown',
  standalone: false,
})

export class AppkitDropdownSearchFilterDirective implements OnInit, OnDestroy {

  private readonly subs = new Subscription();
  private lastFilterResult: any[] = [];

  @ContentChildren(DropdownListItemComponent, { descendants: true })
  private listItems?: QueryList<DropdownListItemComponent>;


  constructor(
    @Host() private readonly dropdown: DropdownComponent,
    private readonly renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    if (!this.dropdown.showSearchbox) {
      return;
    }

    this.subs.add(
      this.dropdown.onFilterResult.subscribe((event: { searchStr?: string; result?: any[] }) => {
        this.lastFilterResult = Array.isArray(event?.result) ? event.result : [];
        this.applyFilter(String(event?.searchStr || ''), this.lastFilterResult);
      })
    );

    this.subs.add(
      this.dropdown.onVisibleChange.subscribe((event: { dropdownStatus?: boolean }) => {
        if (event?.dropdownStatus) {
          const search = String(this.dropdown.searchText || '').trim();
          if (!search) {
            this.showAllItems();
          }
        } else {
          this.lastFilterResult = [];
          this.showAllItems();
        }
      })
    );
  }

  ngAfterContentInit(): void {
    if (!this.dropdown.showSearchbox || !this.listItems) {
      return;
    }


    this.subs.add(
      this.listItems.changes.subscribe(() => {
        const search = String(this.dropdown.searchText || '').trim();
        if (search) {
          this.applyFilter(search, this.lastFilterResult);
        } else {
          this.showAllItems();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyFilter(searchStr: string, result: any[]): void {
    const search = String(searchStr || '').trim();
    const optionValue = this.dropdown.optionValue || 'value';
    const visibleValues = new Set(
      (Array.isArray(result) ? result : []).map((item) => String(item?.[optionValue]))
    );

    this.listItems?.forEach((listItem) => {
      const item = listItem.item;
      const host = this.getListItemHost(listItem);
      if (!host || !item) {
        return;
      }

      listItem.highlightText = search;
      const visible = !search || visibleValues.has(String(item?.[optionValue]));
      this.renderer.setStyle(host, 'display', visible ? '' : 'none');
    });
  }

  private showAllItems(): void {
    this.listItems?.forEach((listItem) => {
      const host = this.getListItemHost(listItem);
      if (!host) {
        return;
      }
      listItem.highlightText = '';
      this.renderer.removeStyle(host, 'display');
    });
  }

  private getListItemHost(listItem: DropdownListItemComponent): HTMLElement | null {
    const elementRef = (listItem as unknown as { el?: { nativeElement?: HTMLElement } }).el;
    return elementRef?.nativeElement ?? null;
  }
}
